import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import api, { apiError } from "@/lib/api";

const STAGES = ["new", "to_contact", "contacted", "prequalification", "interview_scheduled", "interview_completed",
  "evaluation", "orientation", "training", "ready_deployment", "active", "progressing", "not_retained", "inactive"];

export default function AdminPipeline() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [dragging, setDragging] = useState(null);
  const [over, setOver] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/candidates", { params: { limit: 200 } });
    setItems(data.items || []);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const move = async (candidateId, stage) => {
    const previous = items;
    setItems((prev) => prev.map((c) => (c.id === candidateId ? { ...c, pipeline_stage: stage } : c)));
    try {
      await api.put(`/candidates/${candidateId}/stage`, { stage });
      toast.success(t(`stages.${stage}`));
    } catch (error) {
      setItems(previous);
      toast.error(apiError(error, t("common.error")));
    }
  };

  return (
    <div data-testid="admin-pipeline">
      <h1 className="font-display text-3xl font-light sm:text-4xl">{t("admin.pipeline")}</h1>
      <p className="mt-2 text-xs text-muted-foreground">{items.length} {t("admin.resultsCount")}</p>

      <div className="mt-6 flex gap-4 overflow-x-auto pb-6">
        {STAGES.map((stage) => {
          const cards = items.filter((c) => c.pipeline_stage === stage);
          return (
            <div
              key={stage}
              data-testid={`pipeline-column-${stage}`}
              onDragOver={(event) => {
                event.preventDefault();
                setOver(stage);
              }}
              onDragLeave={() => setOver((s) => (s === stage ? null : s))}
              onDrop={() => {
                if (dragging) move(dragging, stage);
                setDragging(null);
                setOver(null);
              }}
              className={`w-64 shrink-0 border bg-muted/40 p-3 transition-colors duration-200 ${
                over === stage ? "border-accent bg-accent/5" : "border-border"
              }`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <h2 className="text-sm font-medium">{t(`stages.${stage}`)}</h2>
                <span className="text-xs tabular-nums text-muted-foreground">{cards.length}</span>
              </div>
              <div className="mt-3 space-y-2">
                {cards.map((candidate) => (
                  <article
                    key={candidate.id}
                    draggable
                    onDragStart={() => setDragging(candidate.id)}
                    onDragEnd={() => setDragging(null)}
                    data-testid={`pipeline-card-${candidate.id}`}
                    className="cursor-grab border border-border bg-surface p-3 text-sm shadow-sm transition-shadow duration-200 hover:shadow-md active:cursor-grabbing"
                  >
                    <Link to={`/${lang}/admin/candidates/${candidate.id}`} className="font-medium hover:text-accent">
                      {candidate.first_name} {candidate.last_name}
                    </Link>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {candidate.city}, {candidate.country}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {candidate.assigned_recruiter_name || t("admin.unassigned")}
                    </p>
                    <select
                      data-testid={`pipeline-move-${candidate.id}`}
                      value={candidate.pipeline_stage}
                      onChange={(event) => move(candidate.id, event.target.value)}
                      className="mt-2 h-8 w-full border border-input bg-surface px-2 text-xs"
                      aria-label={t("admin.changeStage")}
                    >
                      {STAGES.map((key) => (
                        <option key={key} value={key}>{t(`stages.${key}`)}</option>
                      ))}
                    </select>
                  </article>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
